import { ArrowRight, Check } from 'lucide-react'
import Link from 'next/link'

import { PayOnline } from '@/components/molecules/pay-online'
import { formatPlnMinor } from '@/lib/money'
import type { PaymentInstruction } from '@/lib/payment-transfer'
import { ALL_SERVICES_HREF } from '@/lib/service-routes'
import { RADIUS, SURFACE_STRONG } from '@/lib/surface'
import { BODY, META, MICRO, STAT } from '@/lib/typography'
import { cn } from '@/lib/utils'

/** What a placed order hands to this screen. */
export type Placed = {
  orderNumber: string
  hasQuoteOnlyItems: boolean
  /** False when the confirmation could not be delivered — see `checkoutAction`. */
  confirmationSent: boolean
  /** How to pay, or `null` when the basket is all quotes. See `checkoutAction`. */
  payment: PaymentInstruction | null
  /** Where "Zapłać online" goes, or `null` when no gateway is configured. */
  payOnlineHref: string | null
  /** The order's own conversation, or `null` for a guest with no panel. */
  chatHref: string | null
}

/**
 * The order landed.
 *
 * Replaces the whole ordering grid rather than appearing under it: once the
 * order exists, everything above becomes a form for something already done, and
 * leaving it on screen invites a second submission of the same basket.
 *
 * The copy splits on `hasQuoteOnlyItems` because the two outcomes genuinely
 * differ — a basket of editing work has a price and a start date, while one
 * holding a camera for a weekend has neither until somebody checks the shelf.
 */
export function SuccessPanel({ locale, placed }: { locale: string; placed: Placed }) {
  return (
    <div className={cn('mx-auto w-full max-w-xl p-10 text-center', RADIUS.feature, SURFACE_STRONG)}>
      <div
        aria-hidden="true"
        className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-white text-neutral-900"
      >
        <Check size={22} strokeWidth={2.6} />
      </div>

      <h2 className={cn(STAT, 'text-white')}>Dziękujemy! Zaczynamy działać.</h2>
      <p className={cn(BODY, 'mt-3 text-white/65')}>
        {placed.hasQuoteOnlyItems
          ? 'Przygotujemy wycenę wybranych pozycji i odezwiemy się z propozycją realizacji.'
          : 'Realizację zaczynamy, gdy wpłata dotrze na konto. Specjalista odezwie się w ciągu 24 h, żeby ustalić szczegóły.'}
      </p>

      {/* Said plainly rather than left to be discovered by an empty inbox. The
          order is real either way; the sentence promising a letter was not. */}
      {placed.confirmationSent ? null : (
        <p className={cn(META, 'mt-3 text-white/55')}>
          Potwierdzenie e-mail nie wyszło — zapisz numer poniżej. Zamówienie jest przyjęte i mamy
          Twoje dane kontaktowe.
        </p>
      )}

      {/* The human order reference — their receipt. Quoted back to us in any
          message, it finds the order instantly. */}
      <div
        className={cn(
          'mt-6 inline-flex flex-col items-center gap-1 px-6 py-4',
          RADIUS.box,
          SURFACE_STRONG,
        )}
      >
        <span className={cn(MICRO, 'font-semibold tracking-[0.22em] text-white/55 uppercase')}>
          Numer zamówienia
        </span>
        <span className="text-[1.05rem] font-bold tracking-tight text-white">
          {placed.orderNumber}
        </span>
      </div>

      {placed.payment ? (
        <PaymentBlock payment={placed.payment} payOnlineHref={placed.payOnlineHref} />
      ) : null}

      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        {/* The FIRST thing offered, when there is an account to offer it to.
            Every order now opens its own conversation with a greeting already
            in it (`openOrderRoom`), and this is the door to it — without the
            link the room exists and nobody walks in, which is the same as not
            having one. A guest gets no link and no dead end: checkout is open
            to them, the panel is not. */}
        {placed.chatHref ? (
          <Link
            href={placed.chatHref}
            className={cn(
              META,
              'inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-semibold text-neutral-900 transition-colors hover:bg-white/90',
            )}
          >
            Przejdź do rozmowy
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        ) : null}
        <Link
          href={ALL_SERVICES_HREF}
          className={cn(
            META,
            placed.chatHref
              ? 'inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 font-semibold text-white/80 transition-colors hover:bg-white/[0.08] hover:text-white'
              : 'inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-semibold text-neutral-900 transition-colors hover:bg-white/90',
          )}
        >
          Zamów kolejną usługę
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
        <Link
          href={`/${locale}`}
          className={cn(
            META,
            'inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 font-semibold text-white/80 transition-colors hover:bg-white/[0.08] hover:text-white',
          )}
        >
          Wróć na stronę główną
        </Link>
      </div>
    </div>
  )
}

/**
 * How to pay, on the screen that just took the order.
 *
 * Here as well as in the e-mail, not instead of it. The letter is what somebody
 * comes back to on the day they make the transfer; this is for the person who
 * is going to do it right now, in the tab they are already in — and for the one
 * whose mail never arrives, which is a real state while `myflou.pl` is not a
 * verified sender.
 *
 * The account number is monospaced and left exactly as configured, spaces and
 * all: it is a string somebody retypes into a banking app, and a proportional
 * font makes twenty-six digits genuinely easy to lose your place in.
 */
function PaymentBlock({
  payment,
  payOnlineHref,
}: {
  payment: PaymentInstruction
  payOnlineHref: string | null
}) {
  return (
    <div className={cn('mt-6 px-6 py-5 text-left', RADIUS.box, SURFACE_STRONG)}>
      {/* First, because it is the thing somebody can finish right now, in the
          tab they are already in. The account number stays underneath for the
          customer who would rather transfer — the gateway adds a way to pay, it
          does not take one away. */}
      {payOnlineHref ? (
        <div className="mb-5">
          <PayOnline href={payOnlineHref} amountGrossMinor={payment.amountGrossMinor} />
        </div>
      ) : null}
      <p className={cn(MICRO, 'font-semibold tracking-[0.22em] text-white/55 uppercase')}>
        Do zapłaty
      </p>
      <p className="mt-1 text-[1.35rem] font-bold tracking-tight text-white">
        {formatPlnMinor(payment.amountGrossMinor)}
      </p>

      <dl className="mt-4 flex flex-col gap-3">
        <Row label="Odbiorca" value={payment.account.recipient} />
        <Row label="Numer konta" value={payment.account.account} mono />
        {payment.account.bank ? <Row label="Bank" value={payment.account.bank} /> : null}
        <Row label="Tytuł przelewu" value={payment.title} mono />
      </dl>

      <p className={cn(META, 'mt-4 text-white/55')}>
        Termin wpłaty: <span className="font-semibold text-white/80">{payment.deadline}</span>. Do
        tego czasu trzymamy dla Ciebie terminy z zamówienia.
      </p>
    </div>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className={cn(MICRO, 'font-semibold tracking-[0.18em] text-white/55 uppercase')}>
        {label}
      </dt>
      <dd
        className={cn(
          'mt-0.5 break-words text-[0.95rem] font-semibold text-white',
          mono && 'font-mono tracking-[0.04em]',
        )}
      >
        {value}
      </dd>
    </div>
  )
}
